import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { FIFTY2_ID, heroImage, intro, yearCompass as yc } from '../data/fifty2Tuesdays';
import useFifty2Progress from '../components/fifty2/useFifty2Progress';
import DateSettings from '../components/fifty2/DateSettings';
import WeekGrid from '../components/fifty2/WeekGrid';
import WeekView from '../components/fifty2/WeekView';

const YearCompass = () => (
  <section className="bg-[#2b3550] text-[#f4ead8] rounded-3xl p-8 md:p-12 my-12" data-testid="f2-year-compass">
    <p className="font-body text-xs uppercase tracking-[0.35em] text-[#e9b872] mb-4">{yc.eyebrow}</p>
    <p className="font-heading text-2xl md:text-3xl leading-snug mb-6 max-w-2xl">{yc.lead}</p>
    <div className="grid md:grid-cols-2 gap-8 font-body text-[#f4ead8]/85 leading-relaxed">
      <div className="space-y-3">
        {yc.body.map((p) => <p key={p}>{p}</p>)}
        <ul className="space-y-2 pt-1">{yc.strengthen.map((s) => <li key={s} className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#e9b872] flex-shrink-0" />{s}</li>)}</ul>
      </div>
      <div className="space-y-3">{yc.close.map((p) => <p key={p}>{p}</p>)}</div>
    </div>
    <p className="font-heading italic text-xl text-[#e9b872] mt-8">{yc.final}</p>
    <p className="font-body text-sm uppercase tracking-[0.25em] mt-2">{yc.welcome}</p>
  </section>
);

const LockedNote = ({ user }) => (
  <div className="max-w-xl mx-auto text-center bg-white border border-[#e4d8c3] rounded-2xl p-10" data-testid="f2-locked-note">
    <p className="inline-flex items-center gap-1.5 text-xs font-body text-[#b07d3c] uppercase tracking-[0.25em] mb-3"><Lock size={12} /> Full year</p>
    <h2 className="font-heading text-3xl text-[#2b3550] mb-3">Week 1 is yours to try</h2>
    <p className="font-body text-stone-600">{user ? 'To open all 52 weeks, reach out to Jill and she’ll unlock the full year for you.' : 'Log in from the Programs page, then reach out to Jill to unlock the full year.'}</p>
  </div>
);

export default function Fifty2TuesdaysPage() {
  const { user, hasAccess, loading } = useAuth();
  const full = !!user && (hasAccess(FIFTY2_ID) || !!user.is_admin);
  const { progress, status, saveSettings, setReflection, setCompleted } = useFifty2Progress(user?.id);
  const [open, setOpen] = useState(null);
  const [lockedTap, setLockedTap] = useState(0);
  const noteRef = useRef(null);
  const canOpen = (n) => n >= 1 && n <= 52 && (n === 1 || full);
  const go = (n) => {
    if (!canOpen(n)) { setOpen(null); setLockedTap((c) => c + 1); return; }
    setLockedTap(0); setOpen(n); window.scrollTo(0, 0);
  };
  useEffect(() => { if (lockedTap) noteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, [lockedTap]);
  const doneCount = Object.values(progress.weeks).filter((w) => w?.completed_at).length;

  if (loading) return <div className="min-h-screen flex items-center justify-center font-body text-stone-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#faf5ec] pt-24 pb-20 px-4" data-testid="fifty2-page">
      <div className="max-w-6xl mx-auto">
        {open ? (
          <button onClick={() => { setOpen(null); window.scrollTo(0, 0); }} className="inline-flex items-center gap-2 text-[#2b3550]/70 hover:text-[#2b3550] font-body text-sm mb-8" data-testid="f2-back-weeks"><ArrowLeft size={16} /> All 52 weeks</button>
        ) : (
          <Link to="/programs" className="inline-flex items-center gap-2 text-[#2b3550]/70 hover:text-[#2b3550] font-body text-sm mb-8" data-testid="f2-back-programs"><ArrowLeft size={16} /> Back to Programs</Link>
        )}
        {open ? (
          <WeekView n={open} progress={progress} canOpen={canOpen} onNav={go} journal={{ status, onReflect: setReflection, onComplete: setCompleted, signedIn: !!user }} />
        ) : (
          <>
            <img src={heroImage} alt="Fifty2 Tuesdays — A Companion for the Year" className="w-full max-w-3xl mx-auto rounded-3xl" data-testid="f2-hero" />
            <p className="text-center font-body text-stone-600 max-w-xl mx-auto mt-6" data-testid="f2-intro">{intro}</p>
            <YearCompass />
            {user && <div className="mb-8"><DateSettings progress={progress} onSave={saveSettings} doneCount={doneCount} /></div>}
            <h2 className="font-heading text-3xl text-[#2b3550] mb-6">Your 52 Week Journey</h2>
            {lockedTap > 0 && <div ref={noteRef} className="mb-8"><LockedNote user={user} /></div>}
            <WeekGrid progress={progress} canOpen={canOpen} onOpen={go} />
          </>
        )}
      </div>
    </div>
  );
}
